<template>
  <div>
    <section>
      <b-card no-body>
        <b-card-body>
          <b-row class="align-items-end">
            <b-col lg="2">
              <b-form-group :label="$t('Profile Type')" class="mb-0">
                <el-radio-group v-model="profileType" @change="() => { currentPage = 1; reload(); }" size="small">
                  <el-radio-button label="ORGANIZATION">Company</el-radio-button>
                  <el-radio-button label="VENDOR">Vendor</el-radio-button>
                </el-radio-group>
              </b-form-group>
            </b-col>
            <b-col lg="2" v-if="pipelineList.length > 1">
              <b-form-group :label="$t('Pipeline')" class="mb-0">
                <el-select size="small" v-model="selectedPipelineId" clearable placeholder="All Pipelines" style="width: 100%" @change="reload()">
                  <el-option v-for="item in pipelineList" :key="item.id" :label="item.name" :value="item.id"/>
                </el-select>
              </b-form-group>
            </b-col>
            <b-col lg="2">
              <b-form-group :label="$t('Account Code')" class="mb-0">
                <el-input size="small" v-model="filters.accountCode" @keyup.enter.native="reload()" clearable/>
              </b-form-group>
            </b-col>
            <b-col lg="2">
              <b-form-group :label="$t('Company Name')" class="mb-0">
                <el-input size="small" v-model="filters.companyName" @keyup.enter.native="reload()" clearable/>
              </b-form-group>
            </b-col>
            <b-col lg="2">
              <b-form-group :label="$t('Document Type')" class="mb-0">
                <el-select size="small" v-model="filters.documentType" clearable placeholder="All" style="width: 100%">
                  <el-option v-for="item in documentTypeOptions" :key="item.code" :label="item.label" :value="item.label"/>
                </el-select>
              </b-form-group>
            </b-col>
            <b-col class="d-flex align-items-end justify-content-end">
              <el-tooltip :content="showMoreFilters ? 'Hide more filters' : 'More filters'" placement="top">
                <el-button size="small" :type="showMoreFilters ? 'warning' : 'default'" @click="showMoreFilters = !showMoreFilters" style="margin-right: 6px;">
                  <i :class="showMoreFilters ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                </el-button>
              </el-tooltip>
              <el-button type="primary" size="small" @click="reload()">
                <i class="el-icon-search"></i> Search
              </el-button>
              <el-button type="success" size="small" @click="exportExcel()" :loading="isExporting" style="margin-left: 6px;">
                <i class="el-icon-download"></i> Export
              </el-button>
            </b-col>
          </b-row>
          <b-row v-show="showMoreFilters" class="mt-3">
            <b-col lg="4">
              <b-form-group :label="$t('Effective Date')" class="mb-0">
                <el-date-picker size="small" v-model="filters.effectiveDateRange" type="daterange" range-separator="to" start-placeholder="From" end-placeholder="To" value-format="yyyy-MM-dd" style="width: 100%"/>
              </b-form-group>
            </b-col>
            <b-col lg="4">
              <b-form-group :label="$t('Expiry Date')" class="mb-0">
                <el-date-picker size="small" v-model="filters.expiryDateRange" type="daterange" range-separator="to" start-placeholder="From" end-placeholder="To" value-format="yyyy-MM-dd" style="width: 100%"/>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <el-table :data="tableData" style="width: 100%" v-loading="isLoading" :height="window.height - 350" size="small" @sort-change="handleSortChange">
        <el-table-column prop="addTime" label="Create Date" width="130" sortable="custom">
          <template v-slot="scope">
            {{ formatDate(scope.row.addTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="handleByUser" label="PIC" width="120">
          <template v-slot="scope">
            {{ getUserName(scope.row.handleByUser) }}
          </template>
        </el-table-column>
        <el-table-column prop="companyName" label="Client Name" min-width="150" sortable="custom"/>
        <el-table-column prop="accountCode" label="Client Code" width="100"/>
        <el-table-column prop="clientCodeDoc" label="Client Code(DOC)" width="150"/>
        <el-table-column prop="type" label="Type of Doc" width="150"/>
        <el-table-column prop="description" label="Description" min-width="150"/>
        <el-table-column prop="documentNumber" label="Doc Ref No" width="220" sortable="custom"/>
        <el-table-column prop="boxFileNo" label="Box File No" width="100" sortable="custom"/>
        <el-table-column prop="dateOfIssue" label="Effective Date" width="140" sortable="custom"/>
        <el-table-column prop="dateOfExpiry" label="Expiry Date" width="130" sortable="custom"/>
        <el-table-column prop="contractTemplate" label="Contract Template" width="120"/>
        <el-table-column prop="remarks" label="Remarks" min-width="150"/>
        <el-table-column prop="status" label="Status" width="140" sortable="custom"/>
        <el-table-column label="Action Status" width="200" v-if="profileType === 'ORGANIZATION'">
          <template v-slot="scope">
            <div style="font-size: 11px; line-height: 1.4;">
              <div><b>Credit:</b> {{ scope.row.actionCreditApproval || '-' }}</div>
              <div><b>BR:</b> {{ scope.row.actionBrCopy || '-' }}</div>
              <div><b>Search:</b> {{ scope.row.actionCompanySearch || '-' }}</div>
              <div><b>Legal:</b> {{ scope.row.actionLegalReview || '-' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Action Status" width="320" v-if="profileType === 'VENDOR'">
          <template v-slot="scope">
            <div style="font-size: 11px; line-height: 1.4;">
              <div><b>BR:</b> {{ scope.row.actionBrCopy || '-' }}</div>
              <div><b>Search:</b> {{ scope.row.actionCompanySearch || '-' }}</div>
              <div><b>Application:</b> {{ scope.row.actionApplicationForm || '-' }}</div>
              <div><b>Declaration:</b> {{ scope.row.actionDeclarationForm || '-' }}</div>
              <div><b>Assessment:</b> {{ scope.row.actionAssessmentForm || '-' }}</div>
              <div><b>EC/SS:</b> {{ scope.row.actionEcOrSocialSecurity || '-' }}</div>
              <div><b>Legal:</b> {{ scope.row.actionLegalReview || '-' }}</div>
              <div><b>Price:</b> {{ scope.row.actionPriceComparison || '-' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="creditAmount" :label="profileType === 'VENDOR' ? 'Payment Term' : 'Credit Limit'" width="100">
          <template v-slot="scope">
            {{ scope.row.creditAmount ? formatNumber(scope.row.creditAmount) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="creditDay" label="Credit Day" width="100" v-if="profileType !== 'VENDOR'"/>
        <el-table-column prop="deposit" label="Deposit" width="90">
          <template v-slot="scope">
            {{ scope.row.deposit ? formatNumber(scope.row.deposit) : '-' }}
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-2 d-flex justify-content-between align-items-center">
        <span>Total: {{ totalRecords }} records</span>
        <el-pagination
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="pageSize"
          layout="prev, pager, next"
          :total="totalRecords">
        </el-pagination>
      </div>
    </section>
  </div>
</template>

<script>
import apis from "@/configs/apis";
import axios from "axios";
export default {
  data() {
    return {
      window: {
        width: 0,
        height: 0
      },
      isLoading: false,
      isExporting: false,
      showMoreFilters: false,
      profileType: 'ORGANIZATION',
      selectedPipelineId: null,
      pipelineList: [],
      filters: {
        accountCode: '',
        companyName: '',
        documentType: '',
        effectiveDateRange: null,
        expiryDateRange: null,
      },
      tableData: [],
      currentPage: 1,
      pageSize: 100,
      totalRecords: 0,
      sortField: '',
      sortOrder: '',
      documentTypeOptions: [
        { code: 'CON', label: 'Contract' },
        { code: 'QUO', label: 'Quotation' },
        { code: 'RFQ', label: 'RFQ' },
        { code: 'EMI', label: 'Email' },
        { code: 'INV', label: 'Invoice' },
        { code: 'OTH', label: 'Others' },
        { code: 'PA', label: 'Power of Attorney' },
        { code: 'TC', label: 'Terms & Conditions' },
        { code: 'APP', label: 'Application Form' },
        { code: 'LA', label: 'Licence Agreement' },
        { code: 'ADD', label: 'Addendum' },
        { code: 'NDA', label: 'Non-Disclosure Agreement' },
        { code: 'NOT', label: 'Notice' },
        { code: 'BR', label: 'Valid Business Registration Certificate' },
      ],
      usersMap: {},
    };
  },
  created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  mounted() {
    this.loadUsers();
    this.loadPipelines();
    this.reload();
  },
  methods: {
    handleResize() {
      this.window.width = window.innerWidth;
      this.window.height = window.innerHeight;
    },
    loadUsers() {
      axios.get(apis.accountList).then(response => {
        const accounts = response.data.data || [];
        accounts.forEach(user => {
          this.usersMap[user.userId] = user.fullName || user.username;
        });
      }).catch(e => {
        console.error('Error loading users:', e);
      });
    },
    getUserName(userId) {
      if (!userId) return '-';
      return this.usersMap[userId] || userId;
    },
    loadPipelines() {
      axios.get(apis.crmGetPipeline)
        .then((response) => {
          this.pipelineList = response.data.data.data || [];
        })
        .catch((error) => {
          console.error('Error loading pipelines:', error);
        });
    },
    reload() {
      this.isLoading = true;
      const params = {
        profileType: this.profileType,
        pageNumber: this.currentPage - 1,
        pageSize: this.pageSize
      };

      if (this.selectedPipelineId) {
        params.pipelineId = this.selectedPipelineId;
      }
      if (this.filters.accountCode) {
        params.accountCode = this.filters.accountCode;
      }
      if (this.filters.companyName) {
        params.companyName = this.filters.companyName;
      }
      if (this.filters.documentType) {
        params.documentType = this.filters.documentType;
      }
      if (this.filters.effectiveDateRange && this.filters.effectiveDateRange.length === 2) {
        params.effectiveDateFrom = this.filters.effectiveDateRange[0];
        params.effectiveDateTo = this.filters.effectiveDateRange[1];
      }
      if (this.filters.expiryDateRange && this.filters.expiryDateRange.length === 2) {
        params.expiryDateFrom = this.filters.expiryDateRange[0];
        params.expiryDateTo = this.filters.expiryDateRange[1];
      }
      if (this.sortField && this.sortOrder) {
        params.sortField = this.sortField;
        params.sortOrder = this.sortOrder;
      }

      axios.get(apis.crmContractStatus, { params })
        .then((response) => {
          const hiddenTypes = ['Application Form', 'Valid Business Registration Certificate'];
          this.tableData = (response.data.data.data || []).filter(row => !hiddenTypes.includes(row.type));
          this.totalRecords = response.data.data.page ? response.data.data.page.total_records : 0;
        })
        .catch((error) => {
          console.error('Error loading contract status:', error);
          this.$message.error('Failed to load contract status data');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.reload();
    },
    handleSortChange({ prop, order }) {
      this.sortField = prop || '';
      this.sortOrder = order === 'ascending' ? 'asc' : order === 'descending' ? 'desc' : '';
      this.currentPage = 1;
      this.reload();
    },
    viewProfile(row) {
      if (row.profileType === 'ORGANIZATION') {
        this.$router.push({
          name: 'erp-company-profile',
          params: { id: row.profileId }
        });
      } else {
        this.$router.push({
          name: 'erp-vendor-company-profile',
          params: { id: row.profileId }
        });
      }
    },
    exportExcel() {
      this.isExporting = true;
      const params = {
        profileType: this.profileType
      };

      if (this.selectedPipelineId) {
        params.pipelineId = this.selectedPipelineId;
      }
      if (this.filters.accountCode) {
        params.accountCode = this.filters.accountCode;
      }
      if (this.filters.companyName) {
        params.companyName = this.filters.companyName;
      }
      if (this.filters.documentType) {
        params.documentType = this.filters.documentType;
      }
      if (this.filters.effectiveDateRange && this.filters.effectiveDateRange.length === 2) {
        params.effectiveDateFrom = this.filters.effectiveDateRange[0];
        params.effectiveDateTo = this.filters.effectiveDateRange[1];
      }
      if (this.filters.expiryDateRange && this.filters.expiryDateRange.length === 2) {
        params.expiryDateFrom = this.filters.expiryDateRange[0];
        params.expiryDateTo = this.filters.expiryDateRange[1];
      }

      axios.get(apis.crmContractStatus + '/export', { params, responseType: 'blob' })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          const contentDisposition = response.headers['content-disposition'];
          let filename = 'CRM-ContractStatus.xlsx';
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
            }
          }
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          this.$message.error('Failed to export Excel file');
          console.error('Export error:', error);
        })
        .finally(() => {
          this.isExporting = false;
        });
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }
};
</script>
